import React from "react";

import { useUser } from "@components/user-context";
import { type FetchOrder_me_order } from "@north-beam/nb-client-side";
import { MetricFormatters } from "@north-beam/nb-client-side";
import { emptyText, formatDateTime } from "@north-beam/nb-common";
import { formatCurrency } from "@north-beam/nb-common/formatting";
import { SubscriptionValueToLabelMapping } from "@pages/subscriptions-order-tags/subscription-order-tags";
import { CardDetails } from "@shared/card-details";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

export function OrderDetails({ order }: { order: FetchOrder_me_order }) {
  const { user } = useUser();
  const prefs = usePrefs();
  const doAnon = prefs.isDemoMode;
  return (
    <CardDetails
      title="Order Details"
      className="mb-5"
      details={[
        {
          heading: "Date",
          items: [
            order.occurredAt?.value
              ? formatDateTime(order.occurredAt.value)
              : emptyText,
          ],
        },
        {
          heading: "Amount",
          items: [
            `${MetricFormatters.currency(
              order.revenueInDollars,
              user.currencies.currentCurrency,
              {
                precision: "exact",
              },
            )}`,
            `${MetricFormatters.currency(
              order.products.reduce((acc, curr) => {
                const value =
                  Number(curr.priceInDollars) * Number(curr.quantity);
                return acc + value;
              }, 0),
              user.currencies.currentCurrency,
              { precision: "exact" },
            )} (gross merchandise value)`,
          ],
        },
        {
          heading: "Discount Amount",
          items: [
            `${MetricFormatters.currency(
              order.discountValue,
              user.currencies.currentCurrency,
              {
                precision: "exact",
              },
            )}`,
          ],
        },
        {
          heading: "Shipping Amount",
          items: [
            `${MetricFormatters.currency(
              order.shippingValue,
              user.currencies.currentCurrency,
              {
                precision: "exact",
              },
            )}`,
          ],
        },
        {
          heading: "Tax Amount",
          items: [
            `${MetricFormatters.currency(
              order.taxValue,
              user.currencies.currentCurrency,
              {
                precision: "exact",
              },
            )}`,
          ],
        },
        {
          heading: "Refund Amount",
          items: [
            `${MetricFormatters.currency(
              order.refundAmountInDollars,
              user.currencies.currentCurrency,
              {
                precision: "exact",
              },
            )}`,
          ],
        },
        {
          heading: "Discount(s)",
          items: order.discountCodes.length
            ? order.discountCodes.map((item) =>
                generateAnonBase64Hash({
                  value: item,
                  prefix: "discount",
                  doAnon,
                }),
              )
            : ["--"],
        },
        ...(user.featureFlags.showSubscriptionOrderTags
          ? [
              {
                heading: "Subscription",
                items: order.subscriptionType
                  ? [SubscriptionValueToLabelMapping[order.subscriptionType]]
                  : ["--"],
              },
            ]
          : []),
        {
          heading: "Items",
          items: order.products.length
            ? order.products.map(
                (product) =>
                  `${generateAnonBase64Hash({
                    value: product.title,
                    prefix: "product",
                    doAnon,
                  })} (${product.quantity} × ${formatCurrency.exact(
                    product.priceInDollars,
                    {
                      code: user.currencies.currentCurrency,
                    },
                  )})`,
              )
            : ["--"],
        },
      ]}
    />
  );
}
